import React, { useState, useEffect } from 'react';
import { Package, ShoppingBag, ChevronRight, X, CheckCircle, ShieldCheck, Zap } from 'lucide-react';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';

const UserPackages = () => {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        fetchPackages();
    }, []);

    const fetchPackages = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/user/get_packages.php`);
            const data = await response.json();
            if (data.success) {
                setPackages(data.packages);
            }
        } catch (error) {
            console.error('Error fetching packages:', error);
        } finally {
            setLoading(false);
        }
    };

    const viewPackageDetails = (pkg) => {
        setSelectedPackage(pkg);
        setShowModal(true);
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-screen bg-gray-50/50">
                    <div className="relative">
                        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600"></div>
                        <div className="absolute top-0 left-0 h-16 w-16 border-t-2 border-transparent border-blue-400 rounded-full animate-pulse"></div>
                    </div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="min-h-screen bg-gray-50/50 p-6 md:p-12">
                <div className="max-w-7xl mx-auto space-y-12">

                    {/* Hero Header */}
                    <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
                        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-100/50 text-blue-700 text-sm font-semibold mb-4 border border-blue-100">
                            <Zap size={16} className="fill-blue-700" /> Premium Membership
                        </div>
                        <h1 className="text-4xl md:text-6xl font-black text-gray-900 tracking-tight">
                            Choose Your <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Power Pack</span>
                        </h1>
                        <p className="text-lg text-gray-600 leading-relaxed">
                            Unlock exclusive earnings, premium products, and accelerated growth with our professionally curated packages.
                        </p>
                    </div>

                    {/* Packages Grid */}
                    {packages.length === 0 ? (
                        <div className="flex flex-col items-center justify-center py-20 bg-white rounded-3xl border border-gray-100 border-dashed shadow-sm">
                            <div className="w-20 h-20 bg-gray-50 rounded-2xl flex items-center justify-center mb-6">
                                <Package className="text-gray-300" size={40} />
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-2">No Packages Available</h3>
                            <p className="text-gray-500 max-w-md text-center">We are currently updating our package inventory. Please check back soon.</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {packages.map((pkg, idx) => (
                                <div
                                    key={pkg.id}
                                    className="group relative bg-white rounded-[2rem] border border-gray-100 shadow-xl hover:shadow-2xl hover:shadow-blue-900/10 transition-all duration-500 overflow-hidden flex flex-col"
                                >
                                    {/* Popular Badge (Mock logic: if price > 10000 or marked) */}
                                    {idx === 1 && (
                                        <div className="absolute top-0 right-0 bg-gradient-to-l from-orange-500 to-amber-500 text-white text-xs font-bold px-4 py-1.5 rounded-bl-xl z-20 shadow-lg">
                                            MOST POPULAR
                                        </div>
                                    )}

                                    {/* Header Section */}
                                    <div className="p-8 pb-0 relative">
                                        <div className={`absolute top-0 left-0 w-full h-32 opacity-10 bg-gradient-to-b ${idx % 2 === 0 ? 'from-blue-600' : 'from-indigo-600'} to-transparent pointer-events-none`}></div>

                                        <div className="flex justify-between items-start mb-6 relative">
                                            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform duration-500">
                                                <Package className="text-gray-700" size={28} />
                                            </div>
                                            <div className="px-3 py-1 bg-blue-50 text-blue-700 text-xs font-bold rounded-full uppercase tracking-wider border border-blue-100">
                                                {pkg.package_code}
                                            </div>
                                        </div>

                                        <h3 className="text-2xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">{pkg.package_name}</h3>
                                        <div className="flex items-baseline gap-1 mb-6">
                                            <span className="text-4xl font-black text-gray-900">₹{parseFloat(pkg.price).toLocaleString()}</span>
                                            {/* PV Badge */}
                                            <div className="ml-3 flex items-center gap-1.5 px-2.5 py-1 bg-green-50 rounded-lg border border-green-100">
                                                <div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div>
                                                <span className="text-xs font-bold text-green-700">{parseFloat(pkg.pv_points).toLocaleString()} PV</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Divider */}
                                    <div className="w-full h-px bg-gradient-to-r from-transparent via-gray-200 to-transparent my-2"></div>

                                    {/* Features Section */}
                                    <div className="p-8 pt-4 flex-1">
                                        <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-6">Benefits Included</p>
                                        <ul className="space-y-4">
                                            <li className="flex items-start gap-3">
                                                <div className="p-1 rounded-full bg-blue-100 text-blue-600 mt-0.5"><CheckCircle size={14} /></div>
                                                <div>
                                                    <span className="text-sm font-semibold text-gray-700 block">Direct Income</span>
                                                    <span className="text-xs text-gray-500">Earn ₹{parseFloat(pkg.direct_bonus).toLocaleString()} per referral</span>
                                                </div>
                                            </li>
                                            <li className="flex items-start gap-3">
                                                <div className="p-1 rounded-full bg-indigo-100 text-indigo-600 mt-0.5"><CheckCircle size={14} /></div>
                                                <div>
                                                    <span className="text-sm font-semibold text-gray-700 block">Matching Bonus</span>
                                                    <span className="text-xs text-gray-500">{parseFloat(pkg.matching_bonus_percentage)}% binary matching</span>
                                                </div>
                                            </li>
                                            <li className="flex items-start gap-3">
                                                <div className="p-1 rounded-full bg-purple-100 text-purple-600 mt-0.5"><ShoppingBag size={14} /></div>
                                                <div>
                                                    <span className="text-sm font-semibold text-gray-700 block">{pkg.product_count} Premium Products</span>
                                                    <span className="text-xs text-gray-500">Worth the package value</span>
                                                </div>
                                            </li>
                                        </ul>
                                    </div>

                                    {/* Action Button */}
                                    <div className="p-8 pt-0 mt-auto">
                                        <button
                                            onClick={() => viewPackageDetails(pkg)}
                                            className="w-full group/btn relative flex items-center justify-center gap-3 bg-gray-900 hover:bg-black text-white px-6 py-4 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 overflow-hidden"
                                        >
                                            <span className="relative z-10">View Details</span>
                                            <ChevronRight size={18} className="relative z-10 group-hover/btn:translate-x-1 transition-transform" />
                                            {/* Shine Effect */}
                                            <div className="absolute inset-0 -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/10 to-transparent skew-x-12"></div>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Modern Modal */}
                {showModal && selectedPackage && (
                    <div className="fixed inset-0 bg-gray-900/60 backdrop-blur-md z-50 flex items-center justify-center p-4">
                        <div className="bg-white rounded-[2rem] w-full max-w-5xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col md:flex-row animate-fadeIn">
                            {/* Left Side: Summary & Visual */}
                            <div className="md:w-1/3 bg-gray-900 p-8 text-white relative flex flex-col justify-between overflow-hidden">
                                <div className="absolute top-0 right-0 w-64 h-64 bg-blue-600/20 rounded-full blur-3xl -mr-32 -mt-32 pointer-events-none"></div>
                                <div className="relative z-10">
                                    <div className="w-16 h-16 bg-white/10 backdrop-blur rounded-2xl flex items-center justify-center mb-8 border border-white/10">
                                        <Package className="text-white" size={32} />
                                    </div>
                                    <p className="text-blue-400 font-bold tracking-widest text-sm mb-2">{selectedPackage.package_code}</p>
                                    <h2 className="text-4xl font-black mb-4 leading-tight">{selectedPackage.package_name}</h2>
                                    <div className="flex items-center gap-3 mb-8">
                                        <span className="text-3xl font-bold">₹{parseFloat(selectedPackage.price).toLocaleString()}</span>
                                        <span className="px-3 py-1 bg-green-500/20 text-green-400 text-xs font-bold rounded-lg border border-green-500/30">
                                            {parseFloat(selectedPackage.pv_points)} PV
                                        </span>
                                    </div>
                                    <p className="text-gray-400 text-sm leading-relaxed mb-8 border-l-2 border-blue-500 pl-4">
                                        {selectedPackage.description || "Experience the ultimate benefits with this premium package designed to boost your network marketing journey."}
                                    </p>
                                </div>

                                {/* Action Buttons */}
                                <div className="relative z-10 mt-auto space-y-3">
                                    <button
                                        onClick={() => {
                                            window.location.href = `/epin/apply?package=${selectedPackage.package_code}`;
                                        }}
                                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-3 rounded-xl font-bold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                                    >
                                        <ShoppingBag size={18} />
                                        Buy Now
                                    </button>
                                    <button
                                        onClick={() => setShowModal(false)}
                                        className="w-full flex items-center justify-center gap-2 text-white/50 hover:text-white transition-colors text-sm font-medium py-2"
                                    >
                                        <X size={18} /> Close Window
                                    </button>
                                </div>
                            </div>

                            {/* Right Side: Details & Products */}
                            <div className="md:w-2/3 bg-white p-8 overflow-y-auto custom-scrollbar">
                                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                    <ShieldCheck className="text-green-600" size={24} />
                                    Package Benefits
                                </h3>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                                    <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100 text-center hover:border-blue-200 transition-colors">
                                        <p className="text-gray-500 text-xs font-bold uppercase mb-1">Direct Income</p>
                                        <p className="text-xl font-black text-gray-900">₹{parseFloat(selectedPackage.direct_bonus).toLocaleString()}</p>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100 text-center hover:border-indigo-200 transition-colors">
                                        <p className="text-gray-500 text-xs font-bold uppercase mb-1">Matching</p>
                                        <p className="text-xl font-black text-gray-900">{parseFloat(selectedPackage.matching_bonus_percentage)}%</p>
                                    </div>
                                    <div className="p-4 rounded-2xl bg-gray-50 border border-gray-100 text-center hover:border-purple-200 transition-colors">
                                        <p className="text-gray-500 text-xs font-bold uppercase mb-1">Performance</p>
                                        <p className="text-xl font-black text-gray-900">₹{parseFloat(selectedPackage.performance_bonus).toLocaleString()}</p>
                                    </div>
                                </div>

                                <h3 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                    <ShoppingBag className="text-blue-600" size={24} />
                                    Included Products <span className="text-gray-400 text-sm font-normal ml-auto">{selectedPackage.products?.length || 0} Items</span>
                                </h3>

                                <div className="space-y-4">
                                    {selectedPackage.products && selectedPackage.products.length > 0 ? (
                                        selectedPackage.products.map((product, idx) => (
                                            <div key={idx} className="flex p-4 rounded-2xl bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                                                <div className="w-16 h-16 bg-gray-100 rounded-xl flex-shrink-0 flex items-center justify-center text-3xl">
                                                    ??
                                                </div>
                                                <div className="ml-4 flex-1">
                                                    <div className="flex justify-between items-start">
                                                        <div>
                                                            <h4 className="font-bold text-gray-900">{product.product_name}</h4>
                                                            <p className="text-sm text-gray-500">{product.category_name}</p>
                                                        </div>
                                                        <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-lg text-xs font-bold">
                                                            x{product.quantity}
                                                        </span>
                                                    </div>
                                                    <div className="mt-2 flex items-center gap-4 text-xs font-medium text-gray-400">
                                                        <span>Value: ₹{parseFloat(product.price).toLocaleString()}</span>
                                                        <span>�</span>
                                                        <span className="text-green-600">{parseFloat(product.pv_points || 0)} PV</span>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="py-12 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                                            <p className="text-gray-400 font-medium">No physical products included in this package.</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default UserPackages;


